import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useDepartmentSettings } from "./hooks/useDepartmentSettings";
import { DepartmentsList } from "./DepartmentsList";
import { DepartmentError } from "./components/DepartmentError";
import { DepartmentLoading } from "./components/DepartmentLoading";
import { DepartmentDialogs } from "./components/DepartmentDialogs";
import { DeleteDepartmentDialog } from "./components/DeleteDepartmentDialog";
import { Department } from "@/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { UserRole } from "@/types/enums";
import { Building2, Plus } from "lucide-react";

export function DepartmentSettings() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { user } = useAuth();
  const {
    departments,
    isLoading,
    error,
    companyId,
    addDepartment,
    updateDepartment,
    deleteDepartment,
    isAppOwner
  } = useDepartmentSettings();
  
  const openEditDialog = (department: Department) => {
    setSelectedDepartment(department);
    setIsEditDialogOpen(true);
  };

  const openDeleteDialog = (department: Department) => {
    setSelectedDepartment(department);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDepartment) {
      toast.error("Error: No department selected for deletion");
      return false;
    }
    
    setIsSubmitting(true);
    
    
    try {
      const success = await deleteDepartment(selectedDepartment.id);
      if (success) {
        toast.success(`Department "${selectedDepartment.name}" deleted successfully`);
        setIsDeleteDialogOpen(false);
        return true;
      } else {
        toast.error("Failed to delete department");
        return false;
      }
    } catch {
      toast.error("An unexpected error occurred while deleting the department");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canManageDepartments = user?.role === UserRole.HR || user?.role === UserRole.SUPER_ADMIN || isAppOwner;

  if (error) {
    return <DepartmentError error={error} />;
  }

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-semibold text-foreground">Department Management</CardTitle>
            </div>
          </div>
          {canManageDepartments && (
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              size="default"
              variant="secondary"
              className="px-4 h-10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Department
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <DepartmentLoading />
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>Total Departments: {departments.length}</span>
            </div>
            <DepartmentsList 
              departments={departments} 
              onEdit={openEditDialog} 
              onDelete={openDeleteDialog} 
            />
          </div>
        )}
      </CardContent>
      
      <DepartmentDialogs
        addDialogOpen={isAddDialogOpen}
        onAddDialogOpenChange={setIsAddDialogOpen}
        onAddDepartment={addDepartment}
        companyId={companyId}
        editDialogOpen={isEditDialogOpen}
        onEditDialogOpenChange={setIsEditDialogOpen}
        selectedDepartment={selectedDepartment}
        onUpdateDepartment={updateDepartment}
        isSubmitting={isSubmitting}
      />
      
      {selectedDepartment && (
        <DeleteDepartmentDialog
          open={isDeleteDialogOpen}
          onOpenChange={setIsDeleteDialogOpen}
          departmentName={selectedDepartment.name}
          onConfirm={handleConfirmDelete}
          isSubmitting={isSubmitting}
        />
      )}
    </Card>
  );
}
